import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Modal, Button, Space } from "antd";
import { Button as UIButton } from "@pc/ds-react";
import { usePopup } from "../../hooks/usePopup";
import { useTranslation } from "../../hooks/useTranslation";
import { PopupType } from "../../redux/slices/popupSlice/popupSlice";
import { useAppSelector, useAppDispatch } from "src/store/hooks";
import {
  clearPermissionErrorModal,
  clearEligibilityErrorModal,
  setAuthEligibilityData,
  setAffiliateOutboundPending,
  type EligibilityErrorModalContent,
} from "src/redux/slices/userSlice/UserSlice";
import { setCustomerIdEncrypted } from "src/redux/slices/appSlice/AppSlice";
import {
  saveTsClicksApi,
  updateGroupMailingConfirmationApi,
  checkTsEligibilityApi,
} from "src/redux/slices/userSlice/UserApi";
import {
  getSaveTsClickId,
  type SaveTsClicksInput,
  type SaveTsClicksResponse,
} from "src/utils/types";
import { logPfsAnalyticsEvent } from "@/utils/helpers";
import { roundDownRemainingUsage } from "@/utils/utils";
import { API_RESULT, EVENT_NAME, EVENT_SCREEN } from "@/utils/enums";
import { UIModal } from "src/UIKit";
import TransferDisclaimerModal from "../TransferDisclaimerModal";
import TransferToSiteModal from "../TransferToSiteModal";
import SavingsStartModal from "../SavingsStartModal/SavingsStartModal";
import "./PopupManager.scss";
import { routes } from "src/navigation/routePaths";

const ELIGIBILITY_MEGAPHONE_IMG_SRC = "/images/megaphone.png";

const hasEligibilityCustomBody = (modal: EligibilityErrorModalContent): boolean =>
  !!(modal.bodyLink || modal.bodyBeforeLink || modal.body.includes("\n"));

const renderEligibilityBodyParagraphs = (text: string) =>
  text.split("\n").map((line, index) => (
    <p key={index} className="ui-modal__body">
      {line}
    </p>
  ));

const PopupManager: React.FC = () => {
  const { currentPopup, close } = usePopup();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const dispatch = useAppDispatch();
  const permissionErrorModal = useAppSelector(
    (state) => state.user.permissionErrorModal,
  );
  const eligibilityErrorModal = useAppSelector(
    (state) => state.user.eligibilityErrorModal,
  );
  const remainingUsageRoundBasis = useAppSelector(
    (state) => state.app.tsInitialData?.tsRemainingUsageRoundBasis,
  );

  const personalId = useAppSelector((state) => state.user.personalId);

  const transferToSiteData = currentPopup?.data?.transferToSite as
    | {
        userName?: string;
        siteName: string;
        siteLogo?: string;
        balanceFormatted: string;
        onConfirm?: () => void;
        saveTsClicksPayload?: SaveTsClicksInput;
        saveTsClicksResponse?: SaveTsClicksResponse;
      }
    | undefined;
  const isTransferToSitePopup = transferToSiteData != null;

  const [balanceLoading, setBalanceLoading] = useState(false);
  const [freshBalance, setFreshBalance] = useState<string | null>(null);
  const savingsConsentRef = useRef(true);

  useEffect(() => {
    if (!isTransferToSitePopup) {
      setFreshBalance(null);
      setBalanceLoading(false);
      return;
    }

    setBalanceLoading(true);
    dispatch(checkTsEligibilityApi())
      .unwrap()
      .then((response) => {
        const amount = response.feRemainingUsageAmountIl ?? 0;
        const displayAmount = roundDownRemainingUsage(
          amount,
          remainingUsageRoundBasis,
        );
        setFreshBalance(displayAmount.toLocaleString("he-IL"));

        if (personalId) {
          dispatch(
            setAuthEligibilityData({
              personalId,
              remainingUsageAmount: amount,
              groupMailingConfirmation:
                response.feGroupMailingConfirmation ?? false,
            }),
          );
          if (response.feCustomerId) {
            dispatch(setCustomerIdEncrypted(response.feCustomerId));
          }
        }
      })
      .catch(() => {
        setFreshBalance(null);
      })
      .finally(() => {
        setBalanceLoading(false);
      });
  }, [isTransferToSitePopup, dispatch, personalId, remainingUsageRoundBasis]);

  const transferDisclaimerData = currentPopup?.data?.transferDisclaimer as
    | {
        userName?: string;
        siteName: string;
        siteLogo?: string;
        affiliateAggregatorName: string;
        limitAmount?: number;
        /** Timer base from the clicked card's timerBaseRef entry (serverMs in ms). */
        websiteTimerServerMs?: number;
        /** Wall-clock timestamp (Date.now()) when websiteTimerServerMs was recorded. */
        websiteTimerReceivedAt?: number;
        onConfirmStart?: () => void;
        onConfirm: () => void;
      }
    | undefined;
  const isTransferDisclaimerPopup = transferDisclaimerData != null;

  const savingsStartData = currentPopup?.data?.savingsStart as
    | {
        remainingUsageAmount: number;
        groupMailingConfirmation?: boolean;
        onConfirm: () => void;
      }
    | undefined;
  const isSavingsStartPopup = savingsStartData != null;


  useEffect(() => {
    if (currentPopup?.autoClose) {
      const timer = setTimeout(() => {
        currentPopup.onClose?.();
        close(currentPopup.id);
      }, currentPopup.autoClose);

      return () => clearTimeout(timer);
    }
  }, [currentPopup, close]);

  const handleClose = () => {
    if (!currentPopup) return;
    if (currentPopup.onClose) {
      currentPopup.onClose();
    }
    close(currentPopup.id);
  };

  const handleConfirm = () => {
    if (!currentPopup) return;
    if (currentPopup.onConfirm) {
      currentPopup.onConfirm();
    }
    close(currentPopup.id);
  };

  const handleCancel = () => {
    if (!currentPopup) return;
    if (currentPopup.onCancel) {
      currentPopup.onCancel();
    }
    close(currentPopup.id);
  };

  const getModalProps = () => {
    if (!currentPopup) return {};
    const baseProps = {
      open: true,
      title: currentPopup.title,
      onCancel: currentPopup.closable !== false ? handleClose : undefined,
      mask: { closable: currentPopup.maskClosable !== false },
      width:
        currentPopup.width ||
        (isTransferDisclaimerPopup ? 800 : isTransferToSitePopup ? 620 : 520),
      zIndex: currentPopup.zIndex || 1055,
      footer: null,
      className: `popup-manager popup-manager--${currentPopup.type}`,
    };

    if (isTransferDisclaimerPopup || isTransferToSitePopup) {
      return baseProps;
    }

    // Custom buttons or default based on type
    if (currentPopup.buttons && currentPopup.buttons.length > 0) {
      return {
        ...baseProps,
        footer: (
          <Space>
            {currentPopup.buttons.map((button, index) => {
              // Map 'danger' to 'primary' for Ant Design Button compatibility
              const buttonType =
                button.type === "danger" ? "primary" : button.type || "default";

              return (
                <Button
                  key={index}
                  type={
                    buttonType as
                      | "default"
                      | "link"
                      | "text"
                      | "primary"
                      | "dashed"
                      | undefined
                  }
                  danger={button.type === "danger"}
                  onClick={() => {
                    if (button.onClick) {
                      button.onClick();
                    }
                    if (
                      currentPopup.type === PopupType.CONFIRM &&
                      index === 1
                    ) {
                      handleConfirm();
                    } else if (
                      currentPopup.type === PopupType.CONFIRM &&
                      index === 0
                    ) {
                      handleCancel();
                    } else {
                      handleClose();
                    }
                  }}
                >
                  {button.text}
                </Button>
              );
            })}
          </Space>
        ),
      };
    }

    // Default buttons based on type
    switch (currentPopup.type) {
      case PopupType.CONFIRM:
        return {
          ...baseProps,
          footer: (
            <Space>
              <Button onClick={handleCancel}>{t("popup.cancel")}</Button>
              <Button type="primary" onClick={handleConfirm}>
                {t("popup.confirm")}
              </Button>
            </Space>
          ),
        };

      case PopupType.ERROR:
      case PopupType.WARNING:
        return {
          ...baseProps,
          footer: (
            <Button type="primary" onClick={handleClose}>
              {t("popup.understood")}
            </Button>
          ),
        };

      default:
        return {
          ...baseProps,
          footer: (
            <Button type="primary" onClick={handleClose}>
              {t("popup.close")}
            </Button>
          ),
        };
    }
  };

  const handleSavingsConfirm = (groupMailingConsent: boolean) => {
    if (!savingsStartData) return;
    if (!savingsStartData.groupMailingConfirmation && groupMailingConsent) {
      dispatch(
        updateGroupMailingConfirmationApi({
          feGroupMailingConfirmation: true,
        }),
      )
        .unwrap()
        .then(() => {
          logPfsAnalyticsEvent({
            event_name: EVENT_NAME.GROUP_MAIL_APPROVAL,
            event_screen: EVENT_SCREEN.LOGIN,
            status: API_RESULT.SUCCEEDED,
          });
        })
        .catch((error) => {
          logPfsAnalyticsEvent({
            event_name: EVENT_NAME.GROUP_MAIL_APPROVAL,
            event_screen: EVENT_SCREEN.LOGIN,
            status: API_RESULT.FAILED,
            failure_reason: error,
          });
        });
    }
    savingsStartData.onConfirm();
    handleClose();
  };

  if (!permissionErrorModal && !currentPopup && !eligibilityErrorModal) {
    return null;
  }

  return (
    <>
      {permissionErrorModal && (
        <UIModal
          open={true}
          onClose={() => dispatch(clearPermissionErrorModal())}
          title={permissionErrorModal.title}
          body={permissionErrorModal.body}
          footer={permissionErrorModal.footer}
          primaryButton={
            permissionErrorModal.primaryButtonLabel
              ? {
                  label: permissionErrorModal.primaryButtonLabel,
                  onClick: () => {
                    dispatch(clearPermissionErrorModal());
                    navigate(routes.login, {
                      state: {
                        returnTo:
                          permissionErrorModal.loginReturnTo ?? pathname,
                      },
                    });
                  },
                }
              : undefined
          }
        />
      )}
      {eligibilityErrorModal && (() => {
        const modal = eligibilityErrorModal;
        const closeEligibilityModal = () => {
          dispatch(clearEligibilityErrorModal());
          navigate(routes.access, { replace: true });
        };
        const disclaimerNode = modal.showDisclaimer ? (
          <>
            {t("tsEligibility.disclaimer")}{" "}
            <a
              href="https://www.passportcard.co.il"
              target="_blank"
              rel="noopener noreferrer"
            >
              {t("tsEligibility.disclaimerLink")}
            </a>
          </>
        ) : undefined;
        const primaryButton = {
          label: t("tsEligibility.backToHome"),
          onClick: closeEligibilityModal,
        };

        if (hasEligibilityCustomBody(modal)) {
          return (
            <UIModal
              open={!!modal}
              onClose={closeEligibilityModal}
              title={modal.title}
              className="ui-modal--eligibility"
              primaryButton={primaryButton}
              disclaimerNode={disclaimerNode}
            >
              {modal.bodyLink && !modal.bodyBeforeLink ? (
                <p className="ui-modal__body">
                  {modal.body}
                  <a
                    href={modal.bodyLink.href}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {modal.bodyLink.label}
                  </a>
                </p>
              ) : (
                <>
                  {modal.body ? renderEligibilityBodyParagraphs(modal.body) : null}
                  {modal.bodyLink && (
                    <p className="ui-modal__body">
                      {modal.bodyBeforeLink ?? ""}
                      <a
                        href={modal.bodyLink.href}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {modal.bodyLink.label}
                      </a>
                      {modal.bodyAfterLink ?? ""}
                    </p>
                  )}
                </>
              )}
              <div className="ui-modal__icon-wrap">
                <img
                  src={ELIGIBILITY_MEGAPHONE_IMG_SRC}
                  alt=""
                  className="ui-modal__icon"
                  aria-hidden
                />
              </div>
              <div className="ui-modal__actions">
                <UIButton
                  onClick={primaryButton.onClick}
                  variant="primary"
                  fullWidth
                  showEndIcon
                >
                  {primaryButton.label}
                </UIButton>
              </div>
              {disclaimerNode && (
                <div className="ui-modal__disclaimer">{disclaimerNode}</div>
              )}
            </UIModal>
          );
        }

        return (
          <UIModal
            open={!!modal}
            onClose={closeEligibilityModal}
            title={modal.title}
            className="ui-modal--eligibility"
            body={modal.body || undefined}
            encouragementText={modal.encouragementText}
            supportText={modal.supportText}
            primaryButton={primaryButton}
            disclaimerNode={disclaimerNode}
          />
        );
      })()}
      {currentPopup && isSavingsStartPopup && savingsStartData && (
        <UIModal
          open={true}
          onClose={handleClose}
          title={t("savingsStartModal.title")}
          closable={false}
          width={760}
        >
          <SavingsStartModal
            remainingUsageAmount={savingsStartData.remainingUsageAmount}
            showGroupMailingCheckbox={
              !savingsStartData.groupMailingConfirmation
            }
            onConfirm={handleSavingsConfirm}
            onGroupMailingConsentChange={(consent) => {
              savingsConsentRef.current = consent;
            }}
          />
        </UIModal>
      )}
      {currentPopup && !isSavingsStartPopup && (
        <Modal {...getModalProps()}>
          <div className="popup-manager__content">
            {isTransferDisclaimerPopup && transferDisclaimerData ? (
              <TransferDisclaimerModal
                userName={transferDisclaimerData.userName}
                siteName={transferDisclaimerData.siteName}
                siteLogo={transferDisclaimerData.siteLogo}
                affiliateAggregatorName={
                  transferDisclaimerData.affiliateAggregatorName
                }
                limitAmount={transferDisclaimerData.limitAmount}
                websiteTimerServerMs={
                  transferDisclaimerData.websiteTimerServerMs
                }
                websiteTimerReceivedAt={
                  transferDisclaimerData.websiteTimerReceivedAt
                }
                onConfirmStart={transferDisclaimerData.onConfirmStart}
                onConfirm={() => {
                  transferDisclaimerData.onConfirm();
                  handleClose();
                }}
              />
            ) : isTransferToSitePopup && transferToSiteData ? (
              <TransferToSiteModal
                userName={transferToSiteData.userName}
                siteName={transferToSiteData.siteName}
                siteLogo={transferToSiteData.siteLogo}
                balanceFormatted={
                  freshBalance ?? transferToSiteData.balanceFormatted
                }
                balanceLoading={balanceLoading}
                onConfirm={() => {
                  const response = transferToSiteData.saveTsClicksResponse;
                  if (response) {
                    logPfsAnalyticsEvent({
                      event_name: EVENT_NAME.AFFILIATE_CLICK,
                      event_screen: EVENT_SCREEN.HOTELS_AND_ATTRACTIONS,
                      affiliate: {
                        affiliate_type:
                          transferToSiteData.saveTsClicksPayload
                            ?.feAffiliateType ?? "",
                        affiliate_id: String(
                          transferToSiteData.saveTsClicksPayload
                            ?.feAffiliateId ?? "",
                        ),
                        affiliate_name: transferToSiteData.siteName ?? "",
                        affiliate_url: response.feAffiliateUrl ?? "",
                        sub_id: getSaveTsClickId(response),
                        click_id: getSaveTsClickId(response),
                        click_end_date: response.feClickEndDate ?? "",
                        card_closing_date: response.feCardClosingDate ?? "",
                        agent_id:
                          transferToSiteData.saveTsClicksPayload?.feAgentId ??
                          "",
                        campaign_code:
                          transferToSiteData.saveTsClicksPayload
                            ?.feCampaignKey ?? "",
                        UTM_params: "",
                        remaining_usage_amount_il:
                          transferToSiteData.saveTsClicksPayload
                            ?.feRemainingUsageAmount ?? 0,
                        usage_limit_release_date: "",
                      },
                    });
                    if (response.feAffiliateUrl) {
                      dispatch(setAffiliateOutboundPending(true));
                      window.open(
                        response.feAffiliateUrl,
                        "_blank",
                        "noopener,noreferrer",
                      );
                    }
                    handleClose();
                  } else if (transferToSiteData.saveTsClicksPayload) {
                    dispatch(
                      saveTsClicksApi(transferToSiteData.saveTsClicksPayload),
                    )
                      .unwrap()
                      .then((res) => {
                        logPfsAnalyticsEvent({
                          event_name: EVENT_NAME.AFFILIATE_CLICK,
                          event_screen: EVENT_SCREEN.HOTELS_AND_ATTRACTIONS,
                          affiliate: {
                            affiliate_type:
                              transferToSiteData.saveTsClicksPayload!
                                .feAffiliateType,
                            affiliate_id: String(
                              transferToSiteData.saveTsClicksPayload!
                                .feAffiliateId,
                            ),
                            affiliate_name: transferToSiteData.siteName ?? "",
                            affiliate_url: res?.feAffiliateUrl ?? "",
                            sub_id: getSaveTsClickId(res),
                            click_id: getSaveTsClickId(res),
                            click_end_date: res?.feClickEndDate ?? "",
                            card_closing_date: res?.feCardClosingDate ?? "",
                            agent_id:
                              transferToSiteData.saveTsClicksPayload!.feAgentId,
                            campaign_code:
                              transferToSiteData.saveTsClicksPayload!
                                .feCampaignKey,
                            UTM_params: "",
                            remaining_usage_amount_il:
                              transferToSiteData.saveTsClicksPayload!
                                .feRemainingUsageAmount,
                            usage_limit_release_date: "",
                          },
                        });
                        if (res?.feAffiliateUrl) {
                          dispatch(setAffiliateOutboundPending(true));
                          window.open(
                            res.feAffiliateUrl,
                            "_blank",
                            "noopener,noreferrer",
                          );
                        } else if (transferToSiteData.onConfirm) {
                          transferToSiteData.onConfirm();
                        }
                        handleClose();
                      })
                      .catch(() => {
                        if (transferToSiteData.onConfirm) {
                          transferToSiteData.onConfirm();
                        }
                        handleClose();
                      });
                  } else if (transferToSiteData.onConfirm) {
                    transferToSiteData.onConfirm();
                    handleClose();
                  } else {
                    handleClose();
                  }
                }}
              />
            ) : (
              <>
                {currentPopup.message && (
                  <div className="popup-manager__message">
                    {currentPopup.message}
                  </div>
                )}
                {currentPopup.data?.content && (
                  <div className="popup-manager__custom-content">
                    {currentPopup.data.content}
                  </div>
                )}
              </>
            )}
          </div>
        </Modal>
      )}
    </>
  );
};

export default PopupManager;
